/**
 * sendTextToConversation - Envio de mensagem de texto para uma conversa
 *
 * Helper compartilhado que reaproveita exatamente a mesma rota já usada pelo
 * chat (`useChatConversation.handleSendMessage`), para que outros pontos do
 * sistema (ex.: compartilhamento de proposta) enviem texto sem duplicar regra:
 *
 *   1. insert em `messages` (message_type = 'text', status 'pending');
 *   2. envio pelo provider via edge function `send-message` (multi-provider:
 *      o roteamento por slug é feito dentro da própria edge function);
 *   3. atualização do status (`sent` / `failed`) e registro no catálogo de erros.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-12 v1.0.0 - Versão inicial (extração do fluxo de texto do chat).
 */

import { supabase } from '@/integrations/supabase/client';
import { registerSendError } from '@/hooks/useErrorCatalog';
import { createLogger } from '@/lib/logger';

const logger = createLogger('sendTextToConversation');

/** Dados mínimos da conversa necessários para o envio */
export interface SendTextConversation {
  id: string;
  inbox_id: string;
  /** Telefone do contato (1:1) ou identificador externo do grupo */
  phone: string;
  contactId?: string | null;
  providerSlug?: string | null;
}

export interface SendTextParams {
  conversation: SendTextConversation;
  /** Usuário logado (remetente agente) */
  userId: string;
  /** Conteúdo textual da mensagem */
  content: string;
}

export interface SendTextResult {
  messageId: string;
  status: 'sent' | 'failed';
  externalId: string | null;
}

/**
 * Persiste e envia uma mensagem de texto.
 * Lança exceção apenas em falha de persistência; falha do provider é refletida
 * no status 'failed' retornado.
 */
export async function sendTextToConversation(
  params: SendTextParams,
): Promise<SendTextResult> {
  const { conversation, userId, content } = params;

  const trimmed = (content || '').trim();
  if (!trimmed) {
    throw new Error('Mensagem de texto vazia.');
  }

  // PASSO 1: persiste a mensagem
  const { data: messageData, error: insertError } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversation.id,
      sender_type: 'agent',
      sender_id: userId,
      content: trimmed,
      message_type: 'text',
      direction: 'outbound',
      status: 'pending',
    })
    .select()
    .single();

  if (insertError) throw insertError;

  // PASSO 2: envio pelo provider (abstração multi-provider na edge function)
  const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
    body: {
      inbox_id: conversation.inbox_id,
      phone: conversation.phone,
      content: trimmed,
      message_type: 'text',
      // Rastreabilidade/observabilidade
      source_type: 'chat',
      message_id: messageData.id,
      conversation_id: conversation.id,
      contact_id: conversation.contactId ?? undefined,
    },
  });

  // PASSO 3: consolida status
  if (sendError || !sendResult?.success) {
    await supabase.from('messages').update({ status: 'failed' }).eq('id', messageData.id);

    registerSendError({
      errorCode: sendResult?.error_code || (sendError as any)?.code || '500',
      provider: conversation.providerSlug || 'unknown',
      inboxId: conversation.inbox_id,
      messageId: messageData.id,
      conversationId: conversation.id,
      messageType: 'text',
      rawError: sendResult || sendError,
      providerMessage: sendResult?.error || (sendError as Error)?.message,
    });

    logger.error('Falha ao enviar texto pelo provider', {
      conversationId: conversation.id,
      messageId: messageData.id,
    });

    return { messageId: messageData.id, status: 'failed', externalId: null };
  }

  await supabase
    .from('messages')
    .update({
      status: 'sent',
      external_id: sendResult.external_id,
      ...(sendResult.provider_message_id
        ? { metadata: { providerMessageId: sendResult.provider_message_id } }
        : {}),
    })
    .eq('id', messageData.id);

  return {
    messageId: messageData.id,
    status: 'sent',
    externalId: sendResult.external_id ?? null,
  };
}
